/*  eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-redeclare */
import { FC } from 'react';

import {
  InterpolationFunction,
  ThemedStyledProps,
  StyledComponentPropsWithRef,
} from 'styled-components';

import { dashboardTheme } from '../config/theme/theme';

import { DefaultTheme, fromTheme, Theme } from '@aircall/tractor';
import { AvatarVariants } from '@aircall/tractor-v2';
import { VariantType } from '@aircall/tractor/es/utils/styles';

/**
 * Allow to turn any leaf of the theme's branch
 * in a function having the same prototype as "fromTheme"
 */
type ThemeVariables<T> = {
  [P in keyof T]: T[P] extends Record<string, unknown>
    ? ThemeVariables<T[P]>
    : ReturnType<typeof fromTheme>;
};

/**
 * Prevent "type ... reference itself" error
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ThemeBranch extends Record<string, number | string | ThemeBranch> {}

const { colors, ...variables } = dashboardTheme;

/**
 * Allow to turn any leaf of the theme's branch in a function having the same prototype as "fromTheme"
 * @param branch - The theme's branch to use
 * @param path - The path to the theme's leaf
 * @returns The theme's leaf with the same prototype as "fromTheme"
 */
function transformThemeLeavesToFunctions<T>(
  branch: ThemeBranch,
  path: string[] = []
): ThemeVariables<T> {
  return Object.entries(branch).reduce((acc, [key, value]) => {
    const currentPath = [...path, key];
    return Object.assign(acc, {
      [key]:
        typeof value === 'object'
          ? transformThemeLeavesToFunctions(value, currentPath)
          : fromTheme(currentPath.join('.')),
    });
  }, {}) as ThemeVariables<T>;
}

/**
 * Properly typed function to help you get a color from the theme
 * ### Example
 * - color.grey.lighter
 * - color.base.white
 */
export const color: ThemeVariables<typeof colors> = transformThemeLeavesToFunctions<typeof colors>(
  colors,
  ['colors']
);

/**
 * Properly typed function to help you get a variable from the theme
 * ### Example
 * - variable.radii[8]
 * - variable.space
 * - variable.radio.sizes
 */
export const variable: ThemeVariables<Omit<DefaultTheme, 'colors'>> =
  transformThemeLeavesToFunctions(variables as unknown as ThemeBranch);

/**
 * Get a theme's variant created from the initials of a username.
 * @param initials - The initials of the username.
 * @param variants - The variants of the theme (e.g. primary, secondary, grey, green, etc.)
 * @returns A variant picked from the theme based on the initials of the username.
 */
export function computeThemeVariantFromInitials(
  initials: string,
  variants: VariantType[]
): VariantType {
  return variants[(initials.charCodeAt(0) + (initials.charCodeAt(1) || 0)) % variants.length];
}

/**
 * Get a theme's variant created from the initials of a username.
 * @param initials - The initials of the username.
 * @param variants - The variants of the theme (e.g. primary, secondary, grey, green, etc.)
 * @returns A variant picked from the theme based on the initials of the username.
 */
export function computeV2ThemeVariantFromInitials(
  initials: string,
  variants: AvatarVariants[]
): AvatarVariants {
  return variants[(initials.charCodeAt(0) + (initials.charCodeAt(1) || 0)) % variants.length];
}

export type StyledFunction<T> = InterpolationFunction<
  ThemedStyledProps<StyledComponentPropsWithRef<FC<T>>, Theme>
>;

export function hexToRGB(inputHex: string): string {
  let hex = inputHex;

  if (hex.startsWith('#')) {
    hex = hex.slice(1);
  }

  if (hex.length !== 6) {
    throw new Error('Invalid HEX color');
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return `rgb(${r}, ${g}, ${b})`;
}
